// Shared Sales/Purchase headline tile — amount, a change pill, and a This-month-vs-Last-month
// bar pair. Used by every dashboard that shows the "this month vs last month" metrics from
// useSalesPurchaseMetrics (Top Level, Finance). `unfavorable` flips which direction of change
// reads as "bad" (e.g. purchase going up costs more, so its increase should read red not green).
import React from 'react';
import { Loader2 } from 'lucide-react';
import { formatCurrency, formatCompactCurrency } from '../../utilities/formatCurrency';

export const MONTH_COMPARISON_TONES = {
    sales: { badge: 'bg-green-100 dark:bg-green-900/30', icon: 'text-green-600 dark:text-green-500', bar: 'bg-green-500' },
    purchase: { badge: 'bg-[#0d1b5e]/10 dark:bg-[#0d1b5e]/30', icon: 'text-[#0d1b5e] dark:text-blue-300', bar: 'bg-[#0d1b5e] dark:bg-blue-400' },
    income: { badge: 'bg-emerald-100 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400', bar: 'bg-emerald-500' },
    expense: { badge: 'bg-orange-100 dark:bg-orange-900/30', icon: 'text-orange-600 dark:text-orange-400', bar: 'bg-orange-500' },
    margin: { badge: 'bg-violet-100 dark:bg-violet-900/30', icon: 'text-violet-600 dark:text-violet-400', bar: 'bg-violet-500' },
};

// currentLabel / previousLabel / changeLabel default to the month wording; the FY cards pass
// their own (e.g. '2026-27' / '2025-26' / 'last FY'). Bars use absolute values so a negative
// amount (a loss-making margin) still draws.
const MonthComparisonCard = ({
    title, icon: Icon, tone, loading, amount, growth, unfavorable, currentValue, previousValue,
    currentLabel = 'This month', previousLabel = 'Last month', changeLabel = 'last month',
}) => {
    const noChange = !growth;
    const positive = growth > 0;
    const favorable = noChange ? null : (positive ? !unfavorable : unfavorable);
    const maxValue = Math.max(Math.abs(currentValue || 0), Math.abs(previousValue || 0), 1);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-start justify-between">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</p>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${tone.badge}`}>
                    <Icon className={`w-4 h-4 ${tone.icon}`} />
                </div>
            </div>

            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {loading ? <Loader2 className={`w-5 h-5 animate-spin ${tone.icon}`} /> : `₹${formatCurrency(amount)}`}
            </p>

            <span className={`inline-block mt-1.5 mb-3 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${noChange
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                : favorable
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                }`}>
                {noChange ? 'No change' : `${positive ? '↑' : '↓'}${Math.abs(growth)}% vs ${changeLabel}`}
            </span>

            <div className="space-y-1.5">
                {[
                    { label: currentLabel, value: currentValue, fill: tone.bar },
                    { label: previousLabel, value: previousValue, fill: 'bg-gray-300 dark:bg-gray-600' },
                ].map((r) => (
                    <div key={r.label} className="flex items-center gap-2 text-[11px]">
                        <span className="w-16 text-gray-400 dark:text-gray-500 flex-shrink-0">{r.label}</span>
                        <span className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                            <span className={`block h-full rounded-full ${r.fill}`} style={{ width: `${(Math.abs(r.value || 0) / maxValue) * 100}%` }} />
                        </span>
                        <span className="w-10 text-right text-gray-500 dark:text-gray-400 flex-shrink-0">₹{formatCompactCurrency(r.value)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MonthComparisonCard;
